/**
 * PostHog analytics wrapper.
 *
 * Hard rule from `plans/03-architecture/observability.md` and the orchestrator:
 * the user has explicitly forbidden re-using their existing PostHog project
 * ("Nowa App"). Until Barel provisions its own project, **every entry
 * point here must silently no-op** when the appropriate env var is missing.
 *
 * - Web: lazily `posthog.init(key, { api_host })` on first `track()`, then
 *   `posthog.capture(event, props)`.
 * - Worker / Node: minimal HTTP wrapper hitting PostHog `/capture/` so we
 *   don't depend on `posthog-node` (which has not been verified inside
 *   Cloudflare Workers).
 *
 * No project ids, keys, or hosts are baked into source. Runtime config is
 * read on every call.
 *
 * Public surface mirrors the PostHog API used in the spec:
 *   - `track(event, props?)`
 *   - `identify(userId, traits?)`
 *   - `reset()`
 */
import { redactFields } from './redact.js';
function resolveKey(env) {
    return env.POSTHOG_KEY ?? env.POSTHOG_PROJECT_API_KEY;
}
/**
 * Optional override for the env source. The web build reads `import.meta.env`,
 * the worker passes its `Bindings` object via `setAnalyticsEnv`. Tests use it
 * to control behaviour without poisoning real env.
 */
let envOverride = null;
/** Replace the env source. Pass `null` to revert to the default lookup. */
export function setAnalyticsEnv(env) {
    envOverride = env;
    // A new env may flip us between configured/unconfigured — drop cached client.
    webClient = null;
    webIdentified = null;
}
const DEFAULT_HOST = 'https://eu.i.posthog.com';
function readEnv() {
    if (envOverride)
        return envOverride;
    // process.env in Node/Vitest; import.meta.env in Vite when present.
    const fromProcess = (() => {
        if (typeof process !== 'undefined' && process.env) {
            return {
                POSTHOG_KEY: process.env.POSTHOG_KEY,
                POSTHOG_PROJECT_API_KEY: process.env.POSTHOG_PROJECT_API_KEY,
                POSTHOG_HOST: process.env.POSTHOG_HOST,
            };
        }
        return null;
    })();
    if (fromProcess && resolveKey(fromProcess))
        return fromProcess;
    const fromImportMeta = (() => {
        try {
            const meta = import.meta.env;
            if (meta && typeof meta === 'object')
                return meta;
        }
        catch {
            // No-op
        }
        return null;
    })();
    if (fromImportMeta && resolveKey(fromImportMeta))
        return fromImportMeta;
    return fromProcess ?? fromImportMeta ?? {};
}
function isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}
let webClient = null;
let webIdentified = null;
async function ensureWebClient(env) {
    const key = resolveKey(env);
    if (!key)
        return null;
    if (webClient)
        return webClient;
    // Dynamic import keeps `posthog-js` out of Worker bundles.
    const mod = (await import('posthog-js'));
    const client = mod.default ?? mod.posthog;
    if (!client)
        return null;
    // Per `09-observability-prompt.md` constraints + PROJECT-STATE §5:
    //  - autocapture ON, capture_pageview ON for the web app.
    //  - session recording OFF (privacy posture).
    //  - sendDefaultPii surfaces are governed by the host App, but PostHog's
    //    own browser SDK doesn't include IP unless we ask for it.
    client.init(key, {
        api_host: env.POSTHOG_HOST ?? DEFAULT_HOST,
        capture_pageview: true,
        autocapture: true,
        disable_session_recording: true,
        persistence: 'localStorage+cookie',
    });
    webClient = client;
    return client;
}
let workerDistinctId = null;
async function postCapture(env, payload) {
    const host = env.POSTHOG_HOST ?? DEFAULT_HOST;
    // We swallow network errors deliberately — analytics MUST NOT impact request
    // handling. Sentry covers the failure separately.
    try {
        await fetch(`${host.replace(/\/$/, '')}/capture/`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(payload),
        });
    }
    catch {
        /* no-op */
    }
}
// ─── Public API ────────────────────────────────────────────────────────────
/**
 * Capture an analytics event. Silent no-op when `POSTHOG_KEY` is missing.
 * Property values matching the secret-key allowlist are redacted before
 * leaving the runtime.
 */
export function track(event, props = {}) {
    const env = readEnv();
    if (!resolveKey(env))
        return;
    const safeProps = redactFields(props);
    if (isBrowser()) {
        void ensureWebClient(env).then((client) => {
            client?.capture(event, safeProps);
        });
        return;
    }
    // Server runtimes: HTTP capture. Pull a distinct_id off the props if the
    // caller supplied one so cross-runtime events stitch together.
    const distinctIdProp = safeProps['distinct_id'] ?? safeProps['userId'];
    const distinctId = typeof distinctIdProp === 'string' && distinctIdProp.length > 0
        ? distinctIdProp
        : (workerDistinctId ?? 'anonymous');
    void postCapture(env, {
        api_key: resolveKey(env),
        event,
        properties: safeProps,
        distinct_id: distinctId,
        timestamp: new Date().toISOString(),
    });
}
/**
 * Associate the current actor with a stable id. Silent no-op when
 * `POSTHOG_KEY` is missing.
 */
export function identify(userId, traits = {}) {
    const env = readEnv();
    if (!resolveKey(env))
        return;
    const safeTraits = redactFields(traits);
    if (isBrowser()) {
        void ensureWebClient(env).then((client) => {
            client?.identify(userId, safeTraits);
        });
        webIdentified = userId;
        return;
    }
    workerDistinctId = userId;
    // PostHog encodes identify as a `$identify` capture on the server side.
    void postCapture(env, {
        api_key: resolveKey(env),
        event: '$identify',
        properties: { $set: safeTraits, distinct_id: userId },
        distinct_id: userId,
        timestamp: new Date().toISOString(),
    });
}
/**
 * Clear user identification (e.g., on logout). Silent no-op when
 * `POSTHOG_KEY` is missing.
 */
export function reset() {
    const env = readEnv();
    if (!resolveKey(env))
        return;
    if (isBrowser() && webClient) {
        webClient.reset();
        webIdentified = null;
        return;
    }
    workerDistinctId = null;
}
/**
 * Whether `track()` will actually emit. Useful for tests and call sites that
 * want to avoid building expensive payloads when telemetry is off.
 */
export function isAnalyticsEnabled() {
    return Boolean(resolveKey(readEnv()));
}
/**
 * Internal: lets tests verify that we actually called `posthog.identify`.
 * Not part of the stable public surface.
 * @internal
 */
export function __getWebIdentifiedForTests() {
    return webIdentified;
}
//# sourceMappingURL=analytics.js.map